import React from 'react';
import { shape, string, oneOf, number } from 'prop-types';
import styled from 'styled-components';
import { getFoolscap, getTrafalgar } from '@bbc/gel-foundations/typography';
import { Burmese, Bengali, EasternArabic, Nepali, WesternArabic } from '@bbc/psammead-locales/numerals';
import { GEL_SPACING_HLF } from '@bbc/gel-foundations/spacings';
import { GEL_GROUP_5_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_2_SCREEN_WIDTH_MIN, GEL_GROUP_2_SCREEN_WIDTH_MAX, GEL_GROUP_1_SCREEN_WIDTH_MIN, GEL_GROUP_1_SCREEN_WIDTH_MAX, GEL_GROUP_0_SCREEN_WIDTH_MAX } from '@bbc/gel-foundations/breakpoints';
import { C_POSTBOX } from '@bbc/psammead-styles/colours';
import { scriptPropType } from '@bbc/gel-foundations/prop-types';
import { grid } from '@bbc/psammead-styles/detection';
import { getSerifLight } from '@bbc/psammead-styles/font-styles';
import { doubleDigitDefault, doubleDigitMedium, doubleDigitSmall, singleDigitDefault, singleDigitMedium, singleDigitSmall, mediumFontServices, smallFontServices } from '../utilities/rankMinWidth'; // For additional spacing for numerals in the right column because of '10' being double digits

var isOnSecondColumn = function isOnSecondColumn(_ref, supportsGrid) {
  var listIndex = _ref.listIndex,
      numberOfItems = _ref.numberOfItems;
  return supportsGrid ? listIndex > Math.ceil(numberOfItems / 2) : listIndex % 2 === 0;
};

var listHasDoubleDigits = function listHasDoubleDigits(numberOfItems) {
  return numberOfItems > 9;
}; // This checks whether the 2nd column contains a double digit value


var columnIncludesDoubleDigits = function columnIncludesDoubleDigits(props, supportsGrid) {
  return isOnSecondColumn(props, supportsGrid) && listHasDoubleDigits(props.numberOfItems);
}; // Returns a min width for the rank wrapper depending on if the list contains 10 items
// and if the numeral is considered medium/small.


var getRankMinWidth = function getRankMinWidth(_ref2) {
  var service = _ref2.service,
      numberOfItems = _ref2.numberOfItems,
      size = _ref2.size;
  var singleDigitMinWidth = {
    default: singleDigitDefault(size),
    medium: singleDigitMedium,
    small: singleDigitSmall
  };
  var doubleDigitMinWidth = {
    default: doubleDigitDefault(size),
    medium: doubleDigitMedium(size),
    small: doubleDigitSmall
  };
  var rankMinWidth = listHasDoubleDigits(numberOfItems) ? doubleDigitMinWidth : singleDigitMinWidth;

  if (mediumFontServices.includes(service)) {
    return rankMinWidth.medium;
  }

  if (smallFontServices.includes(service)) {
    return rankMinWidth.small;
  }

  return rankMinWidth.default;
}; // Ensures the 5th and 10th rank aligns with each other


var isFiveOrTen = function isFiveOrTen(_ref3) {
  var listIndex = _ref3.listIndex,
      service = _ref3.service,
      numberOfItems = _ref3.numberOfItems;
  return listIndex === 5 || listIndex === 10 ? getRankMinWidth({
    service: service,
    numberOfItems: numberOfItems
  }).group5WithFiveColumns : getRankMinWidth({
    service: service,
    numberOfItems: numberOfItems
  }).group5;
};

var OneColumnWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "OneColumnWrapper",
  componentId: "fs4y6m-0"
})(["@media (max-width:", "){min-width:", ";}@media (min-width:", ") and (max-width:", "){min-width:", ";}@media (min-width:", ") and (max-width:", "){min-width:", ";}@media (min-width:", "){min-width:", ";}"], GEL_GROUP_0_SCREEN_WIDTH_MAX, function (props) {
  return listHasDoubleDigits(props.numberOfItems) ? getRankMinWidth(props).group0WithOneColumn : getRankMinWidth(props).group0;
}, GEL_GROUP_1_SCREEN_WIDTH_MIN, GEL_GROUP_1_SCREEN_WIDTH_MAX, function (props) {
  return listHasDoubleDigits(props.numberOfItems) ? getRankMinWidth(props).group1WithOneColumn : getRankMinWidth(props).group1;
}, GEL_GROUP_2_SCREEN_WIDTH_MIN, GEL_GROUP_2_SCREEN_WIDTH_MAX, function (props) {
  return listHasDoubleDigits(props.numberOfItems) ? getRankMinWidth(props).group2WithOneColumn : getRankMinWidth(props).group2;
}, GEL_GROUP_3_SCREEN_WIDTH_MIN, function (props) {
  return listHasDoubleDigits(props.numberOfItems) ? getRankMinWidth(props).group3WithOneColumn : getRankMinWidth(props).group3;
});
var TwoColumnWrapper = /*#__PURE__*/styled(OneColumnWrapper).withConfig({
  displayName: "TwoColumnWrapper",
  componentId: "fs4y6m-1"
})(["@media (min-width:", "){min-width:", ";}@supports (", "){@media (min-width:", "){min-width:", ";}}"], GEL_GROUP_3_SCREEN_WIDTH_MIN, function (props) {
  return columnIncludesDoubleDigits(props, false) ? getRankMinWidth(props).group3WithTwoColumns : getRankMinWidth(props).group3;
}, grid, GEL_GROUP_3_SCREEN_WIDTH_MIN, function (props) {
  return columnIncludesDoubleDigits(props, true) ? getRankMinWidth(props).group3WithTwoColumns : getRankMinWidth(props).group3;
});
var MultiColumnWrapper = /*#__PURE__*/styled(TwoColumnWrapper).withConfig({
  displayName: "MultiColumnWrapper",
  componentId: "fs4y6m-2"
})(["@media (min-width:", "){min-width:", ";}"], GEL_GROUP_5_SCREEN_WIDTH_MIN, function (props) {
  return listHasDoubleDigits(props.numberOfItems) ? isFiveOrTen(props) : getRankMinWidth(props).group5;
});
var StyledSpan = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledSpan",
  componentId: "fs4y6m-3"
})(["", " ", " position:relative;color:", ";margin:0;padding:0;letter-spacing:", ";"], function (_ref4) {
  var service = _ref4.service;
  return getSerifLight(service);
}, function (_ref5) {
  var script = _ref5.script,
      size = _ref5.size;
  return script && size === 'small' ? getTrafalgar(script) : getFoolscap(script);
}, C_POSTBOX, function (_ref6) {
  var service = _ref6.service;
  return service === 'japanese' && "-".concat(GEL_SPACING_HLF);
});

var serviceNumerals = function serviceNumerals(service) {
  var servicesNonWesternNumerals = {
    bengali: Bengali,
    burmese: Burmese,
    nepali: Nepali,
    pashto: EasternArabic,
    persian: EasternArabic
  };
  return servicesNonWesternNumerals[service] ? servicesNonWesternNumerals[service] : WesternArabic;
};

var getColumnWrapper = function getColumnWrapper(columnWrapper) {
  return {
    oneColumn: OneColumnWrapper,
    twoColumn: TwoColumnWrapper,
    multiColumn: MultiColumnWrapper
  }[columnWrapper];
};

var MostReadRank = function MostReadRank(_ref7) {
  var service = _ref7.service,
      script = _ref7.script,
      listIndex = _ref7.listIndex,
      numberOfItems = _ref7.numberOfItems,
      dir = _ref7.dir,
      columnLayout = _ref7.columnLayout,
      size = _ref7.size;
  var numerals = serviceNumerals(service);
  var rank = numerals[listIndex];
  var RankWrapper = getColumnWrapper(columnLayout);
  return /*#__PURE__*/React.createElement(RankWrapper, {
    listIndex: listIndex,
    service: service,
    numberOfItems: numberOfItems,
    dir: dir,
    size: size
  }, /*#__PURE__*/React.createElement(StyledSpan, {
    service: service,
    script: script,
    size: size
  }, rank));
};

MostReadRank.propTypes = {
  service: string.isRequired,
  script: shape(scriptPropType).isRequired,
  listIndex: number.isRequired,
  numberOfItems: number.isRequired,
  dir: oneOf(['rtl', 'ltr']),
  columnLayout: oneOf(['oneColumn', 'twoColumn', 'multiColumn']),
  size: oneOf(['default', 'small'])
};
MostReadRank.defaultProps = {
  dir: 'ltr',
  columnLayout: 'multiColumn',
  size: 'default'
};
export default MostReadRank;